<template>
  <div class="py-1.5 space-y-1">
    <div
      v-for="med in sortedMedications"
      :key="med.measure_code"
      class="flex items-center gap-1.5 text-xs"
      :class="{ 'opacity-40 line-through': med.has_already_failed }"
    >
      <!-- Measure badge -->
      <span
        class="inline-flex items-center px-1.5 py-0.5 rounded font-semibold shrink-0"
        :class="badgeClass(med)"
      >{{ med.measure_code }}</span>

      <!-- Drug name (clickable to open detail) -->
      <span
        class="sensitive text-primary-600 hover:text-primary-800 hover:underline cursor-pointer truncate"
        :title="med.medication_name"
        @click.stop="openDetail(med)"
      >
        {{ med.medication_name || med.measure_name || '—' }}
      </span>

      <!-- Failed prior year badge -->
      <span v-if="med.failed_prior_year"
        class="inline-flex items-center px-1 py-0.5 rounded bg-red-50 text-red-600 font-medium shrink-0 text-[10px]"
        title="Patient failed this measure last year (PDC &lt; 80%)"
      >Failed PY</span>

      <!-- PDC or FIRSTFILL -->
      <span v-if="med.fill_indicator === 'FIRSTFILL'"
        class="inline-flex items-center px-1 py-0.5 rounded bg-blue-50 text-blue-700 font-medium shrink-0"
      >1st Fill</span>
      <span v-else-if="med.pdc_value != null"
        class="font-medium shrink-0"
        :class="pdcClass(med.pdc_value)"
      >{{ formatPdc(med.pdc_value) }}</span>

      <!-- Last fill date (title shows prescriber) -->
      <span v-if="med.last_filled_dt"
        class="text-gray-400 shrink-0"
        :title="prescriberTitle(med)"
      >
        Last Filled: {{ formatShortDate(med.last_filled_dt) }}
      </span>
    </div>

    <div v-if="!sortedMedications.length" class="text-xs text-gray-400 italic">
      No medication data
    </div>
  </div>
</template>

<script>
import { calendarDate } from '@/utils/common'
export default {
  name: 'MedAdherence_MedicationsCell',
  props: ['params'],
  computed: {
    sortedMedications() {
      const meds = this.params.data?.medications || [];
      return [...meds].sort((a, b) => (b.priority_tier || 0) - (a.priority_tier || 0));
    }
  },
  methods: {
    openDetail(med) {
      if (this.params.context?.openMedAdherenceDetail) {
        this.params.context.openMedAdherenceDetail(this.params.data, med);
      }
    },
    badgeClass(med) {
      if (med.has_already_failed) return 'bg-gray-100 text-gray-500';
      if (med.fill_indicator === 'FIRSTFILL') return 'bg-blue-100 text-blue-700';
      if (med.measure_passed) return 'bg-green-100 text-green-700';
      const tier = med.priority_tier || 0;
      if (tier >= 4) return 'bg-red-100 text-red-700';
      if (tier >= 3) return 'bg-orange-100 text-orange-700';
      return 'bg-gray-100 text-gray-600';
    },
    pdcClass(pdc) {
      if (pdc == null) return 'text-gray-400';
      const val = parseFloat(pdc);
      if (val >= 0.8) return 'text-green-600';
      if (val >= 0.6) return 'text-yellow-600';
      return 'text-red-600';
    },
    formatPdc(pdc) {
      if (pdc == null) return '—';
      return `${Math.round(parseFloat(pdc) * 100)}%`;
    },
    prescriberTitle(med) {
      if (!med.prescriber_name) return '';
      const npi = med.prescriber_npi ? ` (NPI: ${med.prescriber_npi})` : '';
      return `Prescriber: ${med.prescriber_name}${npi}`;
    },
    formatShortDate(dateStr) {
      if (!dateStr) return '';
      const d = calendarDate(dateStr);
      if (isNaN(d)) return dateStr;
      return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
    }
  }
};
</script>
